import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search } from 'lucide-react';
import { productsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { fmtMoney } from '../../utils/currency';

const fmt = fmtMoney;

/**
 * Searchable product field with autocomplete. Typing filters the product
 * list by name/SKU; picking a product calls onSelect with it. When the
 * typed text matches nothing, a "+ Add new product" option opens an inline
 * modal to create the product instantly (name, SKU, description, sales
 * price, purchase price, tax %) — then selects it into the line item.
 */
export default function ProductPicker({
  value, products, onSelect, onCreated,
  priceField = 'sales_price', placeholder = 'Search product...',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', sku: '', description: '', sales_price: '', purchase_cost: '', tax_rate: '', product_type: 'NonInventory', category_id: '' });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    productsAPI.categories().then(setCategories).catch(() => {});
  }, []);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Sync the query text when the selected product changes externally (edit mode).
  useEffect(() => {
    const p = products.find(x => x.id === parseInt(value));
    setQuery(p ? p.name : '');
  }, [value]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? products.filter(p => (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q))
    : products;
  const list = filtered.slice(0, 20);
  const showAddOption = !!q;
  const totalOptions = list.length + (showAddOption ? 1 : 0);

  const openDropdown = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 280) });
    setOpen(true);
    setActive(0);
  };

  // Keep the fixed dropdown aligned on scroll / resize.
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const el = inputRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 280) });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const pick = (p) => { onSelect(p); setOpen(false); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) openDropdown(); else setActive(a => Math.min(a + 1, totalOptions - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      if (!open) { openDropdown(); return; }
      e.preventDefault();
      if (active < list.length) pick(list[active]);
      else if (showAddOption) openAddModal();
    }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const openAddModal = () => {
    setNewProduct({ name: query.trim(), sku: '', description: '', sales_price: '', purchase_cost: '', tax_rate: '', product_type: 'NonInventory', category_id: '' });
    setOpen(false);
    setShowAdd(true);
  };

  const saveNewProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name.trim()) { toast.error('Product name is required'); return; }
    setSaving(true);
    try {
      const res = await productsAPI.create({
        name: newProduct.name.trim(),
        sku: newProduct.sku.trim() || undefined,
        description: newProduct.description.trim() || undefined,
        sales_price: parseFloat(newProduct.sales_price) || 0,
        purchase_cost: parseFloat(newProduct.purchase_cost) || 0,
        tax_rate: parseFloat(newProduct.tax_rate) || 0,
        product_type: newProduct.product_type || 'NonInventory',
        is_tracked: newProduct.product_type === 'Inventory',
        category_id: newProduct.category_id ? parseInt(newProduct.category_id) : null,
      });
      toast.success('Product added');
      setShowAdd(false);
      setOpen(false);
      if (onCreated) await onCreated(res.id);
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="product-picker">
      <div style={{ position: 'relative' }}>
        <Search size={14} className="product-picker-search-icon" />
        <input
          ref={inputRef}
          className="form-control product-picker-input"
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); openDropdown(); }}
          onFocus={openDropdown}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      </div>

      {open && rect && (
        <div className="product-picker-dropdown" style={{ top: rect.top, left: rect.left, width: rect.width }}>
          {list.length === 0 && !showAddOption && (
            <div className="product-picker-empty">No products found</div>
          )}
          {list.map((p, idx) => (
            <div
              key={p.id}
              className={`product-picker-option${idx === active ? ' active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              onMouseEnter={() => setActive(idx)}
            >
              <div>
                <div className="pp-name">{p.name}</div>
                <div className="pp-meta">
                  {(p.sku ? `SKU: ${p.sku}` : p.product_type || '')}
                  {parseFloat(p.tax_rate) ? ` · Tax ${p.tax_rate}%` : ''}
                </div>
              </div>
              <div className="pp-price">{fmt(p[priceField])}</div>
            </div>
          ))}
          {showAddOption && (
            <div
              className={`product-picker-add${active === list.length ? ' active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); openAddModal(); }}
              onMouseEnter={() => setActive(list.length)}
            >
              <Plus size={14} /> Add new product “{query.trim()}”
            </div>
          )}
        </div>
      )}

      {showAdd && createPortal(
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Product</h3>
              <button type="button" className="btn-ghost" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <form onSubmit={saveNewProduct}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name <span className="required">*</span></label>
                    <input className="form-control" autoFocus value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>SKU</label>
                    <input className="form-control" value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select className="form-control" value={newProduct.product_type} onChange={e => setNewProduct({ ...newProduct, product_type: e.target.value })}>
                      <option value="NonInventory">Non-Inventory</option>
                      <option value="Inventory">Inventory</option>
                      <option value="Service">Service</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select className="form-control" value={newProduct.category_id} onChange={e => setNewProduct({ ...newProduct, category_id: e.target.value })}>
                      <option value="">None</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Description</label>
                    <textarea className="form-control" rows={2} value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Sales Price</label>
                    <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={newProduct.sales_price} onChange={e => setNewProduct({ ...newProduct, sales_price: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Purchase Price</label>
                    <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={newProduct.purchase_cost} onChange={e => setNewProduct({ ...newProduct, purchase_cost: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Tax %</label>
                    <input className="form-control" type="number" step="0.01" min="0" value={newProduct.tax_rate} onChange={e => setNewProduct({ ...newProduct, tax_rate: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add Product'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
